/** Wire types of the Bridge API (the shared part is in shared/contract.d.ts). */

import type { BridgeEvent as SharedBridgeEvent, BridgeMode, BridgePreview, BridgeView, ConnectCode, CreatedBridge, ErrorCode, JoinResult, Message, NearbyEvent as SharedNearbyEvent, NearbyOfferInput, PollResult } from '@passwork-bridge/shared';
import type { AppPush } from '@/lib/app';

export type {
  BridgeMode,
  BridgePreview,
  BridgeView,
  ConnectCode,
  CreatedBridge,
  ErrorCode,
  FileMessage,
  JoinResult,
  Message,
  NearbyCloseReason,
  NearbyOfferInput,
  NearbyPeer,
  PollResult,
  PresenceSide,
  Role,
} from '@passwork-bridge/shared';

/** The page's own events on top of the server's: the stream going up or down. */
export type BridgeEvent = SharedBridgeEvent | { readonly type: 'connection'; readonly online: boolean };
export type NearbyEvent = SharedNearbyEvent | { readonly type: 'connection'; readonly online: boolean };

/** A text to post. `id` is chosen by the sender, since the ciphertext is bound to it (see `messageAad`). */
export interface NewMessage {
  readonly id: string;
  readonly kind: 'text';
  readonly body: string;
}

/**
 * A file ready to send: its message id and encrypted header, plus a way to produce each encrypted chunk on demand,
 * so a large file is never in memory at once.
 */
export interface EncryptedFile {
  readonly id: string;
  readonly header: string;
  readonly chunks: number;
  /** Plaintext bytes per chunk (the last one may be shorter); the server must accept the same size. */
  readonly chunkBytes: number;
  /** Total ciphertext bytes over all chunks. */
  readonly size: number;
  chunk(index: number): Promise<Uint8Array<ArrayBuffer>>;
}

/** Called as a file goes out or comes in; `fraction` runs from 0 to 1. */
export type Progress = (fraction: number) => void;

/** What the page can be told besides a server's codes: no answer at all, or an answer it cannot make sense of. */
export type ApiErrorCode = ErrorCode | 'network' | 'internal';

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  /** For a request that never got an answer: the server's host and what the browser said, nothing that names a bridge or a key. */
  readonly detail: string | undefined;

  constructor(code: ApiErrorCode, message: string, status = 0, detail?: string) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.detail = detail;
  }
}

/** A message of a bridge, named with the credentials that reach it. */
export interface MessageRef {
  readonly bridgeId: string;
  readonly token: string;
  readonly messageId: string;
}

export interface ChunkRef extends MessageRef {
  readonly index: number;
}

export interface NewConnectCode {
  readonly bridgeId: string;
  readonly ownerToken: string;
  /** `commitOwnerKey` of the owner's public key: the key itself follows once a device has typed the code. */
  readonly ownerKeyHash: string;
}

export interface ConnectSessionRef {
  readonly bridgeId: string;
  readonly ownerToken: string;
  readonly sessionId: string;
}

export interface JoinRequest {
  readonly code: string;
}

/** The joined device's key, sent after the code and the owner's commitment are in hand. */
export interface GuestKeyRequest extends PollRequest {
  readonly publicKey: string;
  /** How the device describes itself, for the owner to see. */
  readonly device: string;
}

export interface PollRequest {
  readonly sessionId: string;
  readonly pollToken: string;
}

/** A page's Nearby presence, and the offer it takes part in. */
export interface OfferRef {
  readonly token: string;
  readonly offerId: string;
}

/** Bridges: making one, opening it, what is posted to it, and its live events. */
export interface BridgeApi {
  createBridge(ttlSeconds: number, maxViews: number, mode: BridgeMode): Promise<CreatedBridge>;
  getBridge(bridgeId: string, token: string): Promise<BridgeView>;
  /** What the link alone shows before it is opened: no view is spent and nothing sealed comes back. */
  preview(bridgeId: string): Promise<BridgePreview>;
  claim(bridgeId: string): Promise<{ claimToken: string; view: BridgeView }>;
  postMessage(bridgeId: string, token: string, message: NewMessage): Promise<Message>;
  /** Sends an encrypted file chunk by chunk and posts it as one message. */
  postFile(bridgeId: string, token: string, file: EncryptedFile, onProgress?: Progress): Promise<Message>;
  /** One encrypted chunk of a file message. */
  getChunk(chunk: ChunkRef): Promise<Uint8Array<ArrayBuffer>>;
  deleteMessage(message: MessageRef): Promise<void>;
  deleteBridge(bridgeId: string, token: string): Promise<void>;
  /** Live events for an open bridge; `device` is what this page runs on (“iPhone · Safari”), shown to the other side. Returns an unsubscribe function. */
  subscribe(bridgeId: string, token: string, onEvent: (event: BridgeEvent) => void, device?: string): () => void;
}

/** Pairing a second device by a six-letter code (docs/API.md, “Connect codes”). */
export interface ConnectApi {
  createConnectCode(request: NewConnectCode): Promise<ConnectCode>;
  /** The owner shows the public key it committed to, once a device has joined. */
  revealConnectKey(session: ConnectSessionRef & { readonly ownerPublicKey: string }): Promise<void>;
  completeConnect(session: ConnectSessionRef & { readonly wrappedKey: string }): Promise<void>;
  joinWithCode(request: JoinRequest): Promise<JoinResult>;
  /** Once, after `joinWithCode`: only now does the owner hear of the device. */
  sendGuestKey(request: GuestKeyRequest): Promise<void>;
  pollConnect(request: PollRequest): Promise<PollResult>;
}

/**
 * Nearby: this page's presence among the devices on the same network. Events arrive through `onEvent`, the first
 * being `hello` with this page's name and the token for the calls below.
 */
export interface NearbyApi {
  /** Returns a function that leaves. */
  nearby(device: string, preferredName: string | null, onEvent: (event: NearbyEvent) => void, push?: AppPush | null): () => void;
  /** Offers a bridge (owner token) to a device nearby, with a commitment to the owner's public key. */
  nearbyOffer(token: string, input: NearbyOfferInput): Promise<{ readonly offerId: string; readonly expiresAt: string }>;
  /** The device offered to answers with its own public key. */
  nearbyAnswer(offer: OfferRef & { readonly guestPublicKey: string }): Promise<void>;
  /** The owner shows the public key it committed to, once the device has answered. */
  nearbyReveal(offer: OfferRef & { readonly ownerPublicKey: string }): Promise<void>;
  /** …and accepts once the person compared the emoji. */
  nearbyAccept(offer: OfferRef): Promise<void>;
  /** Either side calls the offer off. */
  nearbyDecline(offer: OfferRef): Promise<void>;
  /** The owner hands over the bridge key, wrapped for the other device's public key. */
  nearbyKey(offer: OfferRef & { readonly ownerToken: string; readonly wrappedKey: string }): Promise<void>;
}

/** The native app on this side asks to be notified about the bridge: opened, joined, new messages. */
export interface PushApi {
  subscribePush(bridgeId: string, token: string, push: AppPush): Promise<void>;
}

/**
 * Everything the UI needs from a backend. The HTTP implementation talks to the Node server; the demo implementation
 * runs on the Claude artifact runtime. The UI never knows which one it got, and a hook asks only for the part it uses.
 */
export interface Transport extends BridgeApi, ConnectApi, NearbyApi, PushApi {}
